import type { LocalSettings } from "@app/config/LocalSettings";
import localSettings from "@app/config/LocalSettings";
import type {
  ChatGeometry,
  ChatPersistedState,
  ChatPreferences,
  ChatStoredState,
  ChatVerticalRect,
} from "@app/store/chat/ChatState";
import loggingService from "@app/util/logging";

// Versioned: the geometry written under the unsuffixed key was a size plus an offset
// from the bottom-right corner, which cannot be read as a rect. Bumping the key discards
// those values outright — one reset to the default position — instead of carrying a
// migration for a format that never shipped.
export const CHAT_STATE_KEY = "__MMC_CHAT_STATE_V2__";

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const parseVerticalRect = (value: unknown): ChatVerticalRect | undefined => {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  const { height, top } = value as Record<string, unknown>;
  return isFiniteNumber(height) && isFiniteNumber(top) ? { height, top } : undefined;
};

// A rect is all-or-nothing: a partial one would place the window somewhere nobody chose,
// so anything short of four finite edges falls back to the caller's default.
const parseGeometry = (value: unknown): ChatGeometry | undefined => {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  const { height, left, restore, top, width } = value as Record<string, unknown>;
  if (
    !isFiniteNumber(height) ||
    !isFiniteNumber(left) ||
    !isFiniteNumber(top) ||
    !isFiniteNumber(width)
  ) {
    return undefined;
  }
  return {
    height,
    left,
    restore: parseVerticalRect(restore),
    top,
    width,
  };
};

// Validated key by key, unlike parseGeometry above: a rect has to be all-or-nothing because a
// partial one would place the window somewhere nobody chose, but a missing or corrupt preference
// just falls back to its own default and leaves its sibling alone.
const parsePreferences = (value: unknown): Partial<ChatPreferences> | undefined => {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }
  const { readResponsesAloud, voiceInputEnabled } = value as Record<string, unknown>;
  const preferences: Partial<ChatPreferences> = {};
  if (typeof readResponsesAloud === "boolean") {
    preferences.readResponsesAloud = readResponsesAloud;
  }
  if (typeof voiceInputEnabled === "boolean") {
    preferences.voiceInputEnabled = voiceInputEnabled;
  }
  return preferences;
};

class ChatSettings {
  settings: LocalSettings;

  constructor(settings: LocalSettings) {
    this.settings = settings;
  }

  // Called from the reducer's initial state, i.e. during app boot. It must never throw:
  // a corrupted key would take down the whole app, not just the chat. The read itself
  // (getValue, i.e. localStorage.getItem) has to be inside the try too — a throwing
  // getItem (e.g. a browser blocking storage access) would otherwise escape uncaught
  // and take down module evaluation of reducers.ts, and with it app boot.
  //
  // Every return builds a fresh object, so no caller can reach a shared instance and
  // poison the next read by mutating what it got.
  getState = (): ChatStoredState => {
    try {
      const raw = this.settings.getValue(CHAT_STATE_KEY);
      if (typeof raw !== "string") {
        return { isOpen: false };
      }
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null) {
        return { isOpen: false };
      }
      const { geometry, isOpen, preferences } = parsed as Record<string, unknown>;
      return {
        geometry: parseGeometry(geometry),
        isOpen: typeof isOpen === "boolean" ? isOpen : false,
        preferences: parsePreferences(preferences),
      };
    } catch {
      return { isOpen: false };
    }
  };

  // Merges over what is on disk instead of replacing it: getState() re-reads localStorage, so a
  // write only touches the keys it was actually given, which is what makes it safe for two tabs
  // to write at once. A whole-object write was not — each tab's preferences come from a snapshot
  // taken at its own page load, so a tab writing geometry sent that stale snapshot along and
  // clobbered a preference another tab had just saved.
  setState = (state: Partial<ChatPersistedState>) => {
    try {
      this.settings.setValue(CHAT_STATE_KEY, JSON.stringify({ ...this.getState(), ...state }));
    } catch (error) {
      // localStorage can throw (quota exceeded, Safari private browsing). This is called
      // from the persistence saga (store/chat/sagas.ts) with no sagaMiddleware onError
      // configured, so an uncaught throw here would propagate through chatSaga into
      // rootSaga's all([...]) and cancel every saga in the app. Degrade to "not
      // persisted this session" instead.
      loggingService.error("Failed to persist chat state", error);
    }
  };

  // Geometry is dropped rather than written as a default rect: that default derives from
  // viewport size, which storage must not guess. Preferences survive untouched, being
  // device-local, because setState merges over what is on disk.
  resetWindow = () => {
    this.setState({ geometry: undefined, isOpen: false });
  };
}

const chatSettings = new ChatSettings(localSettings);
export default chatSettings;
